// Skins: how a companion is colored. One list of skins for every companion, plus skins a
// design adds for itself.
//
// A shared skin is a recipe that works on any drawing. The design's main color becomes the
// skin's color, and every other color in the drawing keeps its place in light and dark around
// it: a darker band stays darker, a highlight stays lighter, and colors that differ in the
// drawing stay different shades. So red Clawd is simply red, and red Foldi is a red folder
// with a darker red band.
//
// A design can say how its colors shade, fix single colors of a shared skin, or add skins of
// its own, in design.json:
//   "body": { "tones": { "Y": 0.16, "G": -0.16 } }    lightness of each letter next to the main color
//   "body": { "gap": 0.015 }   the smallest step between two shades (see GAP), for finely shaded textures
//   "skins": {
//     "white": { "colors": { "P": "#3A3A44" } },                        fix a color of a shared skin
//     "black": { "off": true },                                         leave out a shared skin
//     "canary": { "label": "Canary", "colors": { "R": "#F29900" } },    its own skin (needs a label)
//     "incognito": { "label": "Incognito", "colors": { ... }, "wear": { "head": "fedora", "glasses": true } }
//   }
//   "body": { "keep": ["E"] }    letters the shared skins leave as they are (glowing eyes)
//   "body": { "paint": ["T", "U", "arms", "legs"] }   only these are painted (Steve's clothes, not his face)
//   "body": { "opacity": { "X": 0.63 } }   letters drawn see-through, in any skin (the Allay's skirt);
//                                          an own skin's "opacity" replaces it ({}: all solid)
//   "skins": { "herobrine": { "label": "Herobrine", "eyes": { "white": "#FFFFFF", "iris": "#FFFFFF" } } }   eye colors of a skin
// Own skins only replace the colors they list. "wear" puts on a hat (when no other hat is
// chosen) and sunglasses (while awake) with the skin. "fx": "matrix" lets green code rain down
// the body (Termi's Matrix skin), "fx": "charged" a blue swirl drift over it (the charged Creeper).
// "extra" adds little drawings to the body, in letters of the skin's colors, at [col, row] of the
// front and side art (a mooshroom's mushrooms): { "front": [[4, -2, ["rr", "tt"]]], "side": [...] }.
//
// Body pixels carry tags like '@R' (palette letter R), '@arms', '@legs', '@body' (a block body)
// or '@main' (whatever the design's main color is). A role character after the '@' asks for a
// variant of that color: '^' the top row, '_' the bottom row, '~' the darker back side, '+' a
// tint that stands out from it (lighter, or darker on very light colors).

import { hash, rand } from './core.js';
import { mixHex, blendHex, lightness, ramp } from './color.js';
import * as fx from './particles.js';

export const SKINS = {
  original: { label: 'Original' },
  red:      { label: 'Cool red', color: '#D8343F', eye: '#1F0D10' },
  blue:     { label: 'Blue', color: '#3D86E0', eye: '#0D1B2E' },
  green:    { label: 'Green', color: '#3FB56C', eye: '#0E2417' },
  white:    { label: 'White', color: '#F3F0EA', eye: '#2A2622', shade: '#CBC3B8' },
  black:    { label: 'Black', color: '#26262B', eye: '#F3F0EA', hi: '#45454E', dark: 1.35, closed: 1.55 },
  yellow:   { label: 'Yellow', color: '#F2C42E', eye: '#2A2108', stops: ['#2B1B05', '#9A5F0B', '#D8960F', '#F2C42E', '#FFF6CF'] },
  magenta:  { label: 'Magenta', color: '#D23FB0', eye: '#24091E' },
  rainbow:  { label: 'Rainbow', color: '#E05A5A', eye: '#1E1A17', fx: 'rainbow' },
  sparkle:  { label: 'Sparkle', color: '#C7B6F2', eye: '#2A2238', fx: 'sparkle', darkColor: '#A795D6',
    stops: ['#2A2140', '#A795D6', '#C7B6F2', '#E6DCFF', '#FFFFFF'] },
  gold:     { label: 'Gold', color: '#D9A92E', eye: '#2B1F07', fx: 'gold', hi: '#F0CE68', shade: '#A97D18', darkColor: '#A97D18',
    stops: ['#2E2105', '#A97D18', '#D9A92E', '#F0CE68', '#FFF8E1'] },
  // More moving skins. Enchanted keeps the companion's own colors and lays Minecraft's purple
  // enchantment glint over them; the others recolor it like the skins above.
  enchanted: { label: 'Enchanted', fx: 'enchanted' },
  diamond:  { label: 'Diamond', color: '#4AEDD9', eye: '#06302E', fx: 'diamond', hi: '#A1FBE8', shade: '#1E9A9A', darkColor: '#1E9A9A',
    stops: ['#06302E', '#1E9A9A', '#4AEDD9', '#A1FBE8', '#FFFFFF'] },
  lava:     { label: 'Lava', color: '#6E2C17', eye: '#FFD25A', fx: 'lava', stops: ['#140603', '#3A160C', '#6E2C17', '#9A4422', '#D27A3E'] },
  galaxy:   { label: 'Galaxy', color: '#4B3A9E', eye: '#F3EEFF', fx: 'galaxy', stops: ['#0A0820', '#211A55', '#4B3A9E', '#7F6AD8', '#D6CCFF'] },
  aurora:   { label: 'Aurora', color: '#2FD39A', eye: '#08201A', fx: 'aurora' },
};

// Lava: veins of molten rock flowing through a dark crust, hotter toward their middle.
const LAVA = [[1.45, '#FFE680'], [1.05, '#FFA024'], [0.7, '#E04A14']];
// The enchantment glint's purple, laid over the companion's own colors.
const GLINT = '#C58BFF';
// A color this much lighter or darker than the body is one of its features (eyes, a mouth, a
// muzzle). The busy skins (sparkle, lava, galaxy) leave features plain, so the face stays clear.
const FEATURE = 0.2;
const BUSY = new Set(['sparkle', 'lava', 'galaxy']);

// Every skin a companion can wear: the shared ones it doesn't leave out, then its own.
export function skinsFor(design) {
  const mine = design.skins || {};
  const own = Object.entries(mine).filter(([k, s]) => !SKINS[k] && s.label).map(([k]) => k);
  return [...Object.keys(SKINS).filter(k => !mine[k]?.off), ...own];
}

// The Matrix effect: green code running down the body, from the bright head of a drop to its
// fading tail. Each column falls at its own speed, and the code flickers like changing letters.
const RAIN = ['#D8FFE0', '#6BFF95', '#2FD45E', '#1A9940', '#0F6128'];
const RAIN_SPAN = 26;   // rows between one drop and the next in a column
const RAIN_EMPTY = 0.3; // how many columns have no code at all
function matrixRain(c, r, now) {
  const col = c + 64, speed = 3.5 + hash(col, 11, 3) * 4;             // rows per second
  if (hash(col, 2, 9) < RAIN_EMPTY) return null;
  const head = ((now / 1000) * speed + hash(col, 7, 5) * RAIN_SPAN) % RAIN_SPAN;
  const behind = Math.floor((head - ((r % RAIN_SPAN) + RAIN_SPAN) % RAIN_SPAN + RAIN_SPAN) % RAIN_SPAN);
  if (behind >= RAIN.length) return null;
  if (behind > 0 && hash(c + 64, r + 64, Math.floor(now / 110)) < 0.3) return null;   // a gap between letters
  return RAIN[behind];
}

// ---------- the design's own colors ----------
// How often each letter is drawn in an art body.
function letterCounts(art) {
  const count = new Map();
  for (const ch of art.join('')) if (ch !== '.' && ch !== ' ') count.set(ch, (count.get(ch) || 0) + 1);
  return count;
}

// The palette: every color the body is drawn with, by key, and which key is the main color.
function paletteOf(design) {
  const body = design.body;
  if (body.kind !== 'art') return { colors: { body: design.colors.body }, main: 'body', eye: design.colors.eye, tones: {}, keep: new Set() };
  const limbs = { arms: body.arms?.color, hands: body.arms?.hand, legs: body.legs?.color, feet: body.legs?.foot };
  const colors = { ...body.palette, ...Object.fromEntries(Object.entries(limbs).filter(([, c]) => c)) };
  const main = body.main || [...letterCounts(body.art)].sort((a, b) => b[1] - a[1])[0][0];
  const keep = new Set(body.keep || []);
  if (body.paint) for (const k of Object.keys(colors)) if (!body.paint.includes(k)) keep.add(k);
  return { colors, main, eye: body.eyes.color || '#1A1A1A', tones: body.tones || {}, keep };
}

// How much of the drawing each color covers, largest first: [[color, share], ...]. For swatches.
function sharesOf(design, colors, main) {
  if (design.body.kind !== 'art') return [[colors[main], 1]];
  const byColor = new Map();
  let total = 0;
  for (const [ch, n] of letterCounts(design.body.art)) {
    const hex = colors[ch] ?? '#FF00FF';
    byColor.set(hex, (byColor.get(hex) || 0) + n);
    total += n;
  }
  return [...byColor].sort((a, b) => b[1] - a[1]).map(([hex, n]) => [hex, n / total]);
}

// ---------- recoloring by lightness ----------
// The smallest difference in lightness between two different colors. A design whose texture has
// many close shades (a Minecraft mob's fur) sets a smaller "gap", so its shading stays as subtle
// in every skin as in the original instead of being pushed into stripes.
const GAP = 0.07;
const CRAMPED = 0.12;   // a skin this close to white (or black) has no room for lighter (or darker) details

// Pushes offsets apart so different colors stay at least `gap` apart, moving away from 0 (the
// main color), which stays where it is.
function spread(offsets, gap) {
  const out = new Map();
  const side = (list, dir) => {
    let prev = 0;
    for (const [hex, d] of list) { const v = dir > 0 ? Math.max(d, prev + gap) : Math.min(d, prev - gap); out.set(hex, v); prev = v; }
  };
  const all = [...offsets].filter(([, d]) => d !== null);
  side(all.filter(([, d]) => d >= 0).sort((a, b) => a[1] - b[1]), 1);
  side(all.filter(([, d]) => d < 0).sort((a, b) => b[1] - a[1]), -1);
  for (const [hex, d] of offsets) if (d === null) out.set(hex, 0);
  return out;
}

// Where each of the design's colors lands on a skin whose main color has lightness La. Each
// color sits as far from the main color as it does in the drawing (or as the design's tones
// say), where there's room, and they squeeze together where there isn't. On a skin that is
// nearly white (or black), lighter (or darker) details go to the other side instead: a white
// Termi gets a dark prompt rather than an invisible one.
function place(design, La) {
  const { colors, main, tones } = paletteOf(design), ref = lightness(colors[main]), gap = design.body.gap ?? GAP;
  const offsets = new Map();
  for (const [k, hex] of Object.entries(colors)) {
    if (!offsets.has(hex)) offsets.set(hex, hex === colors[main] ? null : (tones[k] ?? lightness(hex) - ref));
  }
  let d = spread(offsets, gap);
  const most = sign => Math.max(0, ...[...d.values()].map(v => v * sign));
  const up = 1 - La, down = La;
  if (most(1) > up && up < CRAMPED && down > up) d = spread(new Map([...d].map(([h, v]) => [h, h === colors[main] ? null : -Math.abs(v)])), gap);
  else if (most(-1) > down && down < CRAMPED && up > down) d = spread(new Map([...d].map(([h, v]) => [h, h === colors[main] ? null : Math.abs(v)])), gap);
  const fitUp = Math.min(1, up / (most(1) || 1)), fitDown = Math.min(1, down / (most(-1) || 1));
  return new Map([...d].map(([h, v]) => [h, La + v * (v > 0 ? fitUp : fitDown)]));
}

// A color drawn a little see-through: '#rrggbb' gets an alpha, a rainbow skin's hsl() too.
const seeThrough = (color, a) => (color[0] === '#'
  ? color.slice(0, 7) + Math.round(a * 255).toString(16).padStart(2, '0')
  : color.replace(')', ` / ${a})`));

// A host can hold the moving skins still (reduced motion, or a visitor's "Still"): they're drawn as
// at one moment and give off no sparkles or embers.
let heldAt = null;
export function holdSkins(on) { heldAt = on ? 0 : null; }

// ---------- a look: a design in a skin, ready to draw ----------
const looks = new WeakMap();

export function lookFor(design, key = 'original') {
  let byKey = looks.get(design);
  if (!byKey) looks.set(design, (byKey = new Map()));
  if (!byKey.has(key)) byKey.set(key, makeLook(design, key));
  return byKey.get(key);
}

// A look with glowing eyes, for a companion that turned evil (fights.js): red, unless its design
// says otherwise ("evil": { "eyes": { "white", "iris" } } in design.json: Steve's turn white,
// Herobrine). Eyes drawn into the picture turn red too ("closed" in its eyes says which letters
// they are), and "colors" in "evil" recolors letters of its drawing (the Ghast's eyes, the
// Warden's tendrils).
const EVIL = { white: '#FF3030', iris: '#9E0000' };
const evilLooks = new WeakMap();
export function evilLook(design, look) {
  if (!evilLooks.has(look)) {
    const own = design.evil || {}, eyes = design.body.eyes || {}, pal = paletteOf(design).colors;
    const drawn = eyes.style === 'none' ? Object.keys(eyes.closed || {}).filter(k => pal[k]) : [];
    const light = Math.max(0, ...drawn.map(k => lightness(pal[k])));
    const colors = { ...Object.fromEntries(drawn.map(k => [k, lightness(pal[k]) > light - 0.05 ? EVIL.white : EVIL.iris])), ...own.colors };
    const notch = design.body.kind !== 'art';                // Clawd's one-pixel eyes: two pixels, glowing
    const base = look.color;
    evilLooks.set(look, { ...look, eyes: { ...look.eyes, ...EVIL, ...(notch ? { iris: '#FF2020', glow: '#FFB8B8' } : {}), ...own.eyes },
      color: (tag, c, r, now) => (tag[0] === '@' && colors[tag.slice(1)] ? colors[tag.slice(1)] : base(tag, c, r, now)) });
  }
  return evilLooks.get(look);
}

// An own skin with drawings of its own, an outfit ("body": its own "art", "side", "arms",
// "legs", "eyes", "behind"... and "items", over the design's; the drawings the same size as the
// design's): the design it's drawn with. Its new letters get their colors from its "colors".
function outfitOf(design, own) {
  const b = design.body, o = own.body || {};
  const part = k => (o[k] && b[k] ? { ...b[k], ...o[k] } : o[k] ?? b[k]);
  const body = { ...b, ...o, side: part('side'), arms: part('arms'), legs: part('legs'), eyes: part('eyes') };
  return { ...design, body, items: { ...design.items, ...own.items } };
}

function makeLook(design, key) {
  const own = design.skins?.[key];
  const drawn = own && (own.body || own.items) ? outfitOf(design, own) : null;
  const shared = SKINS[key] ? key : 'original';
  const skin = SKINS[shared], pal = paletteOf(design), main = pal.main;
  // The skin's static color for every key of the palette.
  const colors = {};
  if (skin.color) {
    const tones = skin.stops ? ramp(skin.stops) : ramp(['#000000', skin.color, '#FFFFFF']);
    const La = lightness(skin.color), at = place(design, La);
    for (const [k, hex] of Object.entries(pal.colors)) colors[k] = hex === pal.colors[main] ? skin.color : tones.at(at.get(hex));
  } else Object.assign(colors, pal.colors);
  for (const k of pal.keep) colors[k] = pal.colors[k];
  Object.assign(colors, own?.colors);
  // Rainbow and aurora keep each color's place in light and dark too, as an offset from its middle lightness.
  const kind = own?.fx || skin.fx || null;
  const rainbowAt = kind === 'rainbow' || kind === 'aurora' ? place(design, 0.6) : null;
  const ref = lightness(pal.colors[main]);
  const features = new Set(Object.entries(pal.colors).filter(([k, hex]) => Math.abs(pal.tones[k] ?? (lightness(hex) - ref)) > FEATURE).map(([k]) => k));
  const glints = new Map();                               // enchanted: each color with the glint over it, by strength
  const glinted = (base, level) => {
    const id = base + level;
    if (!glints.has(id)) glints.set(id, blendHex(base, GLINT, 0.45 + level * 0.15));
    return glints.get(id);
  };
  const bodyEye = design.body.eyes?.style === 'pupil' ? pal.eye : (own?.eye || skin.eye || pal.eye);
  const look = {
    key, label: own?.label || skin.label, fx: kind,
    main: colors[main],
    // A shared color skin's own color (for its swatch); null for the design's own colors and its own skins.
    skinColor: SKINS[key] && skin.color ? skin.color : null,
    shares: sharesOf(drawn || design, colors, main),
    eye: bodyEye,
    closed: mixHex(colors[main], skin.closed ?? 0.6),
    wear: own?.wear || null,
    eyes: own?.eyes || null,                              // eye colors of this skin (Herobrine's white eyes)
    extra: own?.extra || null,                            // little drawings it adds (a mooshroom's mushrooms)
    design: drawn,                                        // an outfit: drawings of its own (see outfitOf)
  };

  const statics = new Map();
  const staticColor = (k, role) => {
    const id = role + k;
    if (statics.has(id)) return statics.get(id);
    const base = colors[k] ?? '#FF00FF', isMain = k === main;    // magenta: a letter missing from the palette
    let c = base;
    if (role === '^' && isMain && skin.hi) c = skin.hi;
    else if (role === '_' && isMain && skin.shade) c = skin.shade;
    else if (role === '~') c = isMain && skin.darkColor ? skin.darkColor : mixHex(isMain && skin.shade ? skin.shade : base, skin.dark ?? 0.78);
    else if (role === '+') c = lightness(base) > 0.8 ? mixHex(base, 0.82) : mixHex(base, 1.55);
    statics.set(id, c);
    return c;
  };

  const tags = new Map();
  const parse = tag => {
    let p = tags.get(tag);
    if (!p) {
      const role = '^_~+'.includes(tag[1]) ? tag[1] : '';
      const k = tag.slice(1 + role.length);
      p = { role, key: k === 'main' ? main : k };
      tags.set(tag, p);
    }
    return p;
  };

  look.color = (tag, c, r, now) => {
    if (heldAt !== null) now = heldAt;
    if (tag === 'E') return look.eye;
    if (tag === 'C') return look.closed;
    const { role, key: k } = parse(tag);
    if (pal.keep.has(k) || (BUSY.has(kind) && features.has(k))) return staticColor(k, role);
    if (kind === 'rainbow') {
      const hue = (((c * 22 + r * 8 - now * 0.12) % 360) + 360) % 360;
      const base = k === main ? 60 : Math.round((rainbowAt.get(pal.colors[k]) ?? 0.6) * 100);
      const l = role === '~' ? base - 15 : role === '+' ? base + 22 : base;
      return `hsl(${hue} 82% ${Math.max(6, Math.min(98, l))}%)`;
    }
    if (kind === 'aurora') {                                     // bands of green, teal and violet drifting across
      const t = now / 1000;
      const hue = 175 + 50 * Math.sin(c * 0.18 - t * 0.6) + 45 * Math.sin(r * 0.32 + c * 0.05 + t * 0.9);
      const base = k === main ? 58 : Math.round((rainbowAt.get(pal.colors[k]) ?? 0.6) * 100);
      const l = role === '~' ? base - 14 : role === '+' ? base + 20 : base;
      return `hsl(${Math.round(hue)} 78% ${Math.max(6, Math.min(96, l))}%)`;
    }
    if (kind === 'lava') {
      const t = now / 1000;
      const v = Math.sin(c * 0.55 + Math.sin(r * 0.4 + t * 0.9) * 1.6 + t * 0.7) + Math.sin(r * 0.6 - c * 0.2 - t * 1.3) - (role === '~' ? 0.4 : 0);
      const vein = LAVA.find(([at]) => v > at);
      if (vein) return vein[1];
    }
    if (kind === 'galaxy') {
      const t = now / 1000;
      if (role !== '~') {                                        // stars, each twinkling at its own pace
        const h = hash(c + 90, r + 90, 5);
        if (h < 0.08 && Math.sin(t * (2 + h * 40) + h * 100) > 0.1) return h < 0.03 ? '#FFFFFF' : '#CFD6FF';
      }
      const v = Math.sin(c * 0.3 + r * 0.22 + t * 0.5) + Math.sin(r * 0.42 - c * 0.17 - t * 0.35);   // nebula clouds
      if (v > 1.1) return role === '~' ? '#6A3696' : '#D46ADF';
      if (v > 0.5) return role === '~' ? '#4A3488' : '#8F5FE0';
    }
    if (kind === 'enchanted') {                                  // the glint: purple light sliding over it
      const t = now / 1000, v = Math.sin((c - r * 0.7) * 0.55 - t * 2.4) + 0.6 * Math.sin((c * 0.4 + r) * 0.9 + t * 1.3) - (role === '~' ? 0.3 : 0);
      if (v > 0.55) return glinted(staticColor(k, role), Math.min(2, Math.floor((v - 0.55) * 2.2)));
    }
    if (role !== '~' && kind === 'diamond') {
      const band = ((now % 3200) / 3200) * 44 - 16;              // a shine sweeping the other way than gold's
      if (Math.abs(c - r * 1.2 - band) < 1) return '#F2FFFD';
      if (hash(c + 70, r + 70, Math.floor(now / 230)) < 0.035) return '#FFFFFF';   // little glints
    }
    if (role !== '~' && kind === 'gold') {
      const band = ((now % 2600) / 2600) * 40 - 14;            // a shine that sweeps across
      if (Math.abs(c + r * 1.4 - band) < 1.1) return '#FFF5CC';
    }
    if (role !== '~' && kind === 'sparkle') {
      const g = hash(c + 40, r + 40, Math.floor(now / 170));    // glitter that changes all the time
      if (g < 0.07) return '#FFFFFF';
      if (g < 0.22) return '#E6DCFF';
    }
    if (role !== '~' && kind === 'charged') {                    // a charged creeper's blue swirl drifting over it
      const t = now / 1000, v = Math.sin((c + r) * 0.75 - t * 4) + Math.sin(c * 0.5 - r * 0.35 + t * 1.7);
      if (v > 0.9) return v > 1.55 ? '#D2F0FF' : '#5FB2FF';
    }
    if (role !== '~' && kind === 'matrix' && k !== 'arms' && k !== 'legs') {   // the code runs on the body, not the limbs
      const rain = matrixRain(c, r, now);
      if (rain) return rain;
    }
    return staticColor(k, role);
  };
  // See-through parts ("opacity": { "X": 0.63 } in its body: the Allay's misty skirt) are drawn
  // that opaque, in whatever color the skin gives them. Its own skin can say otherwise
  // ("opacity": {} in the skin: the Slime's magma cube is solid).
  const opacity = own?.opacity || design.body.opacity;
  if (opacity) {
    const solid = look.color;
    look.color = (tag, c, r, now) => {
      const color = solid(tag, c, r, now), a = tag[0] === '@' ? opacity[parse(tag).key] : undefined;
      return a === undefined ? color : seeThrough(color, a);
    };
  }
  return look;
}

// ---------- effects around the body ----------
// Sparkle throws little stars around, gold and diamond glint once per shine, lava gives off
// embers, an enchanted companion purple sparks, and stars now and then float off a galaxy.
const EMBERS = ['#FFE680', '#FFA024', '#E04A14'];
const RUNES = ['#C58BFF', '#E2C8FF', '#9A6AE0'];
function rising(me, now, key, every, colors, from = 0.5) {
  if (now < (me[key] || 0)) return;
  me[key] = now + rand(every * 0.6, every * 1.4);
  const b = me.bbox, s = me.s;
  fx.emit({ kind: 'square', x: rand(b.l, b.r), y: rand(b.t, b.t + (b.b - b.t) * from), vx: rand(-1, 1) * s, vy: -rand(5, 9) * s,
    life: rand(500, 900), size: rand(0.5, 0.9), color: colors[Math.floor(Math.random() * colors.length)], shrink: true, fade: 0.8 });
}
export function skinEffects(me, now) {
  const b = me.bbox, s = me.s, kind = me.look.fx;
  if (!b || me.alpha <= 0 || heldAt !== null) return;
  if (kind === 'lava') rising(me, now, 'nextEmber', 380, EMBERS);
  if (kind === 'enchanted') rising(me, now, 'nextRune', 520, RUNES, 1);
  if (kind === 'galaxy' && now > (me.nextStar || 0)) {
    me.nextStar = now + rand(700, 1400);
    fx.star(rand(b.l - 2 * s, b.r + s), rand(b.t - 2 * s, b.b), rand(500, 800));
  }
  if (kind === 'diamond') {
    const cycle = Math.floor(now / 3200);
    if (cycle !== me.diamondCycle) { me.diamondCycle = cycle; fx.star(b.l + 2 * s, b.t, 520); }
  }
  if (kind === 'sparkle' && now > (me.nextStar || 0)) {
    me.nextStar = now + rand(160, 360);
    fx.star(rand(b.l - 2 * s, b.r + s), rand(b.t - 2 * s, b.b), rand(450, 750));
  }
  if (kind === 'gold') {
    const cycle = Math.floor(now / 2600);
    if (cycle !== me.goldCycle) { me.goldCycle = cycle; fx.star(b.r - 2 * s, b.t, 520); }
  }
  designAura(me, now);
  if (me.evil) rising(me, now, 'nextEvil', 420, ['#FF2A2A', '#FF7A7A', '#8E1B2C'], 0.45);   // evil (fights.js): red sparks off it
}

// A design's own effect, all the time ("aura" in its body: the Blaze's smoke and sparks): little
// squares rising from part of it, each kind at its own rate. An emitter is
// { "area": [left, top, right, bottom] as fractions of its drawn size, "every": ms, "colors" (a
// color, or "@X" for palette letter X in its skin), "size": [min, max], "life": ms, "rise" and
// "drift" (its pixels per second), "fade", "awake": true (not while it sleeps) }.
function designAura(me, now) {
  const aura = (me.look.design || me.design).body.aura, b = me.bbox, s = me.s;   // (an outfit's own, if it has one)
  if (!aura) return;
  me.auraNext ||= [];
  aura.forEach((a, i) => {
    if ((a.awake && me.sleeping) || now < (me.auraNext[i] || 0)) return;
    me.auraNext[i] = now + a.every * rand(0.6, 1.4);
    const [l, t, r, btm] = a.area || [0, 0, 1, 1], [lo, hi] = a.size || [0.6, 1.2];
    const tag = a.colors[Math.floor(Math.random() * a.colors.length)];
    fx.emit({ kind: 'square', x: b.l + rand(l, r) * (b.r - b.l), y: b.t + rand(t, btm) * (b.b - b.t),
      vx: rand(-1, 1) * (a.drift ?? 1) * s, vy: -(a.rise ?? 6) * rand(0.6, 1.2) * s, life: (a.life ?? 800) * rand(0.8, 1.2),
      size: rand(lo, hi), color: tag[0] === '@' ? me.look.color(tag, 0, 0, now) : tag, shrink: true, fade: a.fade ?? 0.85 });
  });
}
